import type { ReactNode } from 'react';
import { Dropdown } from 'react-bootstrap';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import type { VendorType } from '@econtractor/types';
import * as apiVendors from 'api/apiVendors';
import { useToast } from 'context/ToastContext';
import { useSettings } from 'context/SettingsContext';
import SvgEdit from 'components/svg/SvgEdit';
import SvgTrash from 'components/svg/SvgTrash';
import SolarIcon from 'components/icons/SolarIcon';
import { useVendors } from './context/VendorsProvider';
import useDeleteVendor from './hooks/useDeleteVendor';
import { vendorErrMessageKey } from './model/vendorErrMessage';

interface VendorRowActionsProps {
    vendor: VendorType;
    fetchVendors: (force?: boolean) => Promise<void>;
    setEditVendorId: (vendorId: number) => void;
    setSubjectsVendorId: (vendorId: number) => void;
    setPersonsVendorId: (vendorId: number) => void;
}

const VendorRowActions = ({
    vendor,
    fetchVendors,
    setEditVendorId,
    setSubjectsVendorId,
    setPersonsVendorId,
}: VendorRowActionsProps) => {
    const { t } = useTranslation(['Vendors', 'common']);
    const { showToast } = useToast();
    const { getSettings } = useSettings();
    const { setShowSpinner } = useVendors();
    const { deleteVendor } = useDeleteVendor();

    const cbDeleteVendor = async () => {
        setShowSpinner(true);
        const resDelete = await apiVendors.deleteVendor(vendor.ID);
        if (resDelete?.stat) {
            await fetchVendors(true);
            await getSettings();
            setShowSpinner(false);
            showToast(t('messVendorDeleted'), 'success');
            return;
        }

        setShowSpinner(false);
        const errKey = vendorErrMessageKey(resDelete?.errCode);
        showToast(errKey ? t(errKey) : t('common:messError'), 'danger');
    };

    const renderMenuItem = (
        icon: ReactNode,
        title: string,
        subtitle: string,
        onSelect: () => void,
        options?: { danger?: boolean },
    ) => (
        <Dropdown.Item
            as="button"
            className={options?.danger ? 'text-danger' : undefined}
            onClick={(e) => {
                onSelect();
                e.currentTarget.blur();
            }}
        >
            <div className="d-flex align-items-start w-100 text-start">
                <span
                    className={`d-inline-flex align-items-center justify-content-center flex-shrink-0 me-2 item-icon${
                        options?.danger ? ' text-danger' : ''
                    }`}
                >
                    {icon}
                </span>
                <div className="flex-grow-1 min-w-0">
                    <div className={`fw-medium ${options?.danger ? 'text-danger' : ''}`}>{title}</div>
                    <small className="text-muted warehouse-team-menu-hint">{subtitle}</small>
                </div>
            </div>
        </Dropdown.Item>
    );

    const menu = (
        <Dropdown.Menu
            className="p-2 shadow-sm warehouse-team-menu"
            popperConfig={{ strategy: 'fixed' }}
            renderOnMount
        >
            {renderMenuItem(
                <SvgEdit />,
                t('vendorPopup.edit'),
                t('vendorPopup.editHint'),
                () => setEditVendorId(vendor.ID),
            )}

            {renderMenuItem(
                <SolarIcon name="person-vcard" />,
                t('vendorPopup.subjects'),
                t('vendorPopup.subjectsHint'),
                () => setSubjectsVendorId(vendor.ID),
            )}

            {renderMenuItem(
                <SolarIcon name="people" />,
                t('vendorPopup.persons'),
                t('vendorPopup.personsHint'),
                () => setPersonsVendorId(vendor.ID),
            )}

            <Dropdown.Divider className="my-2" />

            {renderMenuItem(
                <SvgTrash />,
                t('vendorPopup.delete'),
                t('vendorPopup.deleteHint'),
                () => deleteVendor(vendor, cbDeleteVendor),
                { danger: true },
            )}
        </Dropdown.Menu>
    );

    return (
        <Dropdown align="end">
            <Dropdown.Toggle className="p-1 py-0" size="sm" variant="light-secondary" id={`dropdown-vendor-${vendor.ID}`}>
                <SolarIcon name="three-dots" />
            </Dropdown.Toggle>

            {typeof document !== 'undefined' ? createPortal(menu, document.body) : menu}
        </Dropdown>
    );
};

export default VendorRowActions;
